// src/app/components/auth/PermissionGuard.tsx
//
// Guard de rota baseado em módulo (AppModule — ver permissionService.ts).
// Uso: <PermissionGuard module="secretaria"><Pagina /></PermissionGuard>
// Redireciona para /sem-acesso se o usuário não tiver o módulo habilitado.
//
// `redirectTo` (opcional, default '/sem-acesso') existe pra rotas onde
// negar acesso não deveria significar "trancar o usuário fora do app" —
// hoje usado só pela landing "/" (ver routes.tsx): um operador sem o
// módulo 'secretaria' configurado no setor cai em '/workspace' (sempre
// acessível, ver ALWAYS_VISIBLE_MODULES no eleveia-api) em vez de
// '/sem-acesso', que fica fora do AppLayout e não tem sidebar/logout —
// beco sem saída pra quem só tem essas telas básicas liberadas.
//
// `bypassRoles` (opcional) existe pra rotas onde o módulo de UI não tem a
// granularidade certa: 'secretaria' gate tanto o Painel de Atendimento
// quanto Documentos/Matrículas/Visão Geral, mas o Painel de Atendimento
// (inbox) já é protegido corretamente pelo backend por setor
// (SessionViewSet.get_queryset, eleveia-api — operador só recebe sessões
// sem setor ou do(s) seu(s) setor(es); testado em test_sector_routing.py).
// Adicionar uma segunda trava por módulo ali só bloqueia operadores de
// setores que nunca tiveram 'secretaria' habilitado, mesmo tendo
// conversas reais esperando resposta. `bypassRoles` deixa esses roles
// passarem direto, sem esperar/checar `allowed_modules` — não é fail-open
// (que é sobre falha de rede), é reconhecer que o módulo não é o
// mecanismo de controle certo pra essa rota específica.
//
// Falha ao buscar /auth/me/permissions/ (rede instável, etc.) nunca deve
// trancar o usuário fora da própria aplicação — nesse caso o guard libera
// o acesso (fail-open) em vez de redirecionar; a mesma página normalmente
// já tem seus próprios guards de role (ex: `if (role !== 'manager') return
// null`), então esse fail-open não expõe nada que já não dependa de outro
// controle de acesso mais abaixo.

import React from 'react';
import { Navigate } from 'react-router';
import { usePermissions, useHasModule } from '../../contexts/PermissionContext';

interface PermissionGuardProps {
  module: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  redirectTo?: string;
  bypassRoles?: string[];
}

export function PermissionGuard({ module, children, fallback, redirectTo = '/sem-acesso', bypassRoles }: PermissionGuardProps) {
  const { permissions, loading, error } = usePermissions();
  const hasAccess = useHasModule(module);
  const bypassed = !!(bypassRoles && permissions?.role && bypassRoles.includes(permissions.role));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full w-full py-24">
        <div className="size-6 border-2 border-slate-300 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }
  if (error) return <>{children}</>;
  if (!hasAccess && !bypassed) {
    return fallback ? <>{fallback}</> : <Navigate to={redirectTo} replace />;
  }
  return <>{children}</>;
}

export function ManagerGuard({ children }: { children: React.ReactNode }) {
  const { permissions, loading } = usePermissions();
  if (loading) return null;
  if (!permissions?.is_manager) return <Navigate to="/sem-acesso" replace />;
  return <>{children}</>;
}

export function useModuleAccess(moduleSlug: string) {
  const { permissions, loading } = usePermissions();
  return {
    loading,
    hasAccess: permissions?.allowed_modules.includes(moduleSlug) ?? false,
    isManager: permissions?.is_manager ?? false,
  };
}
